import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { AnalyticalVariableLookupItem } from '../api/types';
import { useAnalyticalVariableLookupQuery } from '../api/useAnalyticalVariableLookupQuery';
import { selectSources } from '../state/selectors';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type { AnalyticalVariablePickerOption, UseVariablePickerOptionsResult } from '../types';

const toTypeLabel = (format: string | null | undefined): string | null =>
  format ? format.toUpperCase() : null;

// Fetches the project's analytical-variable library and derives which are
// selectable (not already a staged source) and which of those match the
// current search query. Isolated from selection/confirm so the list/search
// behavior can be tested independently -- mirrors useCohortPickerOptions.
export const useVariablePickerOptions = (projectId: string): UseVariablePickerOptionsResult => {
  const intl = useIntl();
  const {
    data: lookupResponse,
    isLoading,
    isError,
  } = useAnalyticalVariableLookupQuery({ projectId });
  const stagedSources = useAnalysisDefinitionStore(selectSources);

  const alreadyStagedAvIds = useMemo(
    () =>
      new Set(
        Object.values(stagedSources ?? {}).flatMap((source) =>
          source.kind === 'variable' ? [source.avId] : []
        )
      ),
    [stagedSources]
  );

  const allOptions = useMemo<AnalyticalVariablePickerOption[]>(
    () =>
      (lookupResponse?.data ?? [])
        .filter(
          (item: AnalyticalVariableLookupItem) => !alreadyStagedAvIds.has(item.analyticalVariableId)
        )
        .map((item: AnalyticalVariableLookupItem) => ({
          analyticalVariableId: item.analyticalVariableId,
          title: item.dataElement ?? item.name,
          format: toTypeLabel(item.format),
          description: item.description,
        })),
    [lookupResponse, alreadyStagedAvIds]
  );

  const [searchQuery, setSearchQuery] = useState('');
  const onSearchChange = useCallback((next: string) => setSearchQuery(next), []);

  const options = useMemo<AnalyticalVariablePickerOption[]>(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return allOptions;
    return allOptions.filter((option) => option.title.toLowerCase().includes(query));
  }, [allOptions, searchQuery]);

  const emptyMessage =
    !isLoading && !isError && allOptions.length === 0
      ? intl.formatMessage({ id: 'analysisDetail.addVariableModal.empty' })
      : null;

  const noResultsMessage =
    !isLoading && !isError && allOptions.length > 0 && options.length === 0
      ? intl.formatMessage({ id: 'analysisDetail.addVariableModal.noResults' })
      : null;

  return {
    allOptions,
    options,
    searchQuery,
    onSearchChange,
    isLoading,
    isError,
    emptyMessage,
    noResultsMessage,
  };
};
